// uploadProposal.jsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createLegalResearchCommitteReport, updateInternalLegalResearchRequest, updateLegalResearchRequest, updateLegalResearchRequestCommittee } from "services/legalResearchService";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import { formatDay } from "utils/helpers";

interface uploadProposalProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
    onUploadProposal: (data: {
        proposalSummary: string;
        proposalFile: string;
    }) => void;
}

const UploadProposal: React.FC<uploadProposalProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onUploadProposal,
    onClose,
}) => {
    const { t } = useTranslation();
    const [proposalSummary, setProposalSummary] = useState(request.proposal_summary || "");
    const [proposalFile, setProposalFile] = useState(
        new Date().toISOString().split("T")[0]
    );
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const contextData = useAuth();

    // Create a ref for the modal content div
    const modalContentRef = useRef<HTMLDivElement>(null);

    // Effect to handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                modalContentRef.current &&
                !modalContentRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setValidationError(null);

        setSubmitting(true);

        try {
            const data = new FormData();
            if (request.request_summary) {
                data.append("research_committe_report_summary", proposalSummary);
                data.append("research_committe_report", request.id);
                data.append("committee_report_file", proposalFile);
                await createLegalResearchCommitteReport(contextData.token, data);
            }
            if (request.status === LEGAL_RESEARCH_REQUEST_STATUS.proposal_update.value)
                data.append(
                    "status",
                    LEGAL_RESEARCH_REQUEST_STATUS.research_underway.value
                );
            else if (request.status === LEGAL_RESEARCH_REQUEST_STATUS.proposal_revision.value)
                data.append(
                    "status",
                    LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value
                );
            else if (request.request_summary)
                data.append(
                    "status",
                    LEGAL_RESEARCH_REQUEST_STATUS.research_committee_underway.value
                );
            else
                data.append(
                    "status",
                    LEGAL_RESEARCH_REQUEST_STATUS.proposal_requested.value
                );
            let response: any;
            if (request.request_summary)
                response = await updateLegalResearchRequestCommittee(request.id, data, contextData.token);
            else {
                data.append("proposal_summary", proposalSummary);
                data.append("proposal_file", proposalFile);
                if (request.requested_by === "internal")
                    response = await updateInternalLegalResearchRequest(contextData.token, request.id, data);
                else
                    response = await updateLegalResearchRequest(request.id, data, contextData.token);
            }
            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
                urgency_level: request?.urgency_level
                    ? request?.urgency_level.charAt(0).toUpperCase() +
                    request?.urgency_level.slice(1).toLowerCase()
                    : "High",
            }));

            showToast(t("LegalResearchProposalUploaded"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadLegalResearchProposal"), "error");
        } finally {
            setSubmitting(false);
        }

        onUploadProposal({ proposalSummary, proposalFile });
        onClose();
    };

    const handleFileChange = (
        file: any,
    ) => {
        setProposalFile(file);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {request.request_summary ? t("UploadReport") : t("UploadProposal")}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-4">
                            <label
                                htmlFor="remark"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {request.request_summary ? t("ReportSummary") : t("ProposalSummary")}
                            </label>
                            <textarea
                                id="remark"
                                className="w-full p-3 border border-gray-300 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere")}
                                value={proposalSummary}
                                onChange={(e) => setProposalSummary(e.target.value)}
                            ></textarea>
                        </div>

                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {request.request_summary ? t("ReportFile") : t("ProposalFile")}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>
                    </div>
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md flex items-center justify-center"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t("Submitting")}
                                </>
                            ) : (
                                t("Submit")
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadProposal;